import * as z from "zod";

// Basic action types
export const ActionEnum = z.enum(["create", "read", "update", "delete"]);

// Field level permission schema
export const FieldPermissionSchema = z.object({
  read: z.boolean().default(false),
  write: z.boolean().default(false),
});

// Resource field schema
export const ResourceFieldSchema = z.object({
  id: z.string().uuid(),
  resource_id: z.string().uuid(),
  name: z.string().min(1, "Field name is required"),
  description: z.string().optional(),
  sensitive: z.boolean().default(false),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

// Base resource schema
export const ResourceBaseSchema = z.object({
  id: z.string().uuid(),
  name: z
    .string()
    .min(1, "Resource name is required")
    .max(50, "Resource name must be less than 50 characters"),
  description: z.string().optional(),
  fields: z.array(ResourceFieldSchema).default([]),
  conditions: z.array(z.string()).default([]),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

// Complete resource schema
export const ResourceSchema = ResourceBaseSchema;

// User permission schema for a specific resource
export const UserPermissionSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  resource_id: z.string().uuid(),
  can_create: z.boolean().default(false),
  can_read: z.boolean().default(false),
  can_update: z.boolean().default(false),
  can_delete: z.boolean().default(false),
  field_permissions: z.record(FieldPermissionSchema).default({}),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
  // Include relations
  user: z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().email(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    role: z.enum(["admin", "staff", "user"]),
    image: z.string().nullable(),
  }),
  resource: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    fields: z.array(ResourceFieldSchema),
  }),
});

// Schema for creating a new resource
export const createResourceSchema = ResourceBaseSchema.omit({
  id: true,
  created: true,
  updated: true,
});

// Schema for updating an existing resource
export const updateResourceSchema = ResourceBaseSchema.partial().omit({
  id: true,
  created: true,
  updated: true,
});

// Schema for creating a new user permission
export const createUserPermissionSchema = UserPermissionSchema.omit({
  id: true,
  created: true,
  updated: true,
});

// Schema for updating an existing user permission
export const updateUserPermissionSchema = UserPermissionSchema.partial().omit({
  id: true,
  user_id: true,
  resource_id: true,
  created: true,
  updated: true,
});

// Schema for updating permissions
export const updatePermissionsSchema = z.object({
  userId: z.string().uuid(),
  resourceName: z.string(),
  permissions: z.object({
    create: z.boolean().optional(),
    read: z.boolean().optional(),
    update: z.boolean().optional(),
    delete: z.boolean().optional(),
    fields: z.record(FieldPermissionSchema).optional(),
  }),
});
